import { Stack, useTheme } from "@mui/material";
import { type BarLabelProps, BarPlot, ChartContainer } from "@mui/x-charts";
import { useEffect, useRef, useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

const INCOME_GREEN = "#71C388";
const EXPENSE_RED = "#FB9784";

const CustomBarLabel = ({
	x,
	y,
	width,
	height,
	yOrigin,
	className,
	children,
	dataIndex,
	color,
}: BarLabelProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const gap = 30;

	const MIN_ZERO_BAR_HEIGHT = 50;
	const isZero = height === 0;

	const effY = isZero ? y - MIN_ZERO_BAR_HEIGHT : y;
	const effHeight = isZero ? MIN_ZERO_BAR_HEIGHT : height;

	const cx = x + width / 2;
	const isPositive = effY < yOrigin;
	const cy = isPositive ? effY - gap : effY + effHeight + gap;

	const title =
		dataIndex === 0
			? t(Resources.BarChart.income)
			: t(Resources.BarChart.expenses);

	return (
		<>
			{isZero && (
				<rect
					x={x}
					y={effY}
					width={width}
					height={effHeight}
					fill={color}
					rx={2}
					pointerEvents="none"
				/>
			)}
			<g transform={`translate(${cx}, ${cy})`} className={className}>
				<text
					y={-12}
					fontSize={12}
					fontWeight={400}
					textAnchor="middle"
					fill={theme.palette.deprecated.grey[500]}
				>
					{title}
				</text>
				<text
					y={10}
					textAnchor="middle"
					fontSize={14}
					fontWeight={600}
					fill={theme.palette.deprecated.grey[700]}
				>
					{children}
				</text>
			</g>
		</>
	);
};

const useWidthObserver = () => {
	const ref = useRef<HTMLDivElement | null>(null);
	const [width, setWidth] = useState(0);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const ro = new ResizeObserver((entries) => {
			const cr = entries[0]?.contentRect;
			if (cr) setWidth(Math.round(cr.width));
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	return { ref, width };
};

type Props = {
	data: number[];
	height?: number;
};

export const MyPlannBarChart = ({ data, height }: Props) => {
	const { t } = useResource();
	const allZero = data.every((v) => v === 0);
	const chartHeight = allZero ? 150 : 300;
	const { ref, width } = useWidthObserver();
	const plotData = data.map((v) => (v === 0 ? (allZero ? 0.5 : 0.0001) : v));

	const maxValue = Math.max(...data);
	const yAxisSpec = allZero
		? [{ min: 0, max: 1, position: "none" as const }]
		: [{ min: 0, max: maxValue * 1.2, position: "none" as const }];

	return (
		<Stack width="100%">
			<div ref={ref}>
				{width > 0 && (
					<ChartContainer
						width={width}
						height={height ?? chartHeight}
						series={[{ type: "bar", data: plotData }]}
						xAxis={[
							{
								scaleType: "band",
								data: ["A", "B"],
								position: "none",
								categoryGapRatio: 0.1,
								barGapRatio: 0,
								colorMap: {
									type: "ordinal",
									values: ["A", "B"],
									colors: [INCOME_GREEN, EXPENSE_RED],
								},
							},
						]}
						yAxis={yAxisSpec}
						margin={{ top: 40, right: 0, bottom: 0, left: 0 }}
					>
						<BarPlot
							barLabel={(item) =>
								item.value != null
									? moneyFormatter(
											allZero ? 0 : item.value,
											t(Resources.Common.CurrencySign),
											`0 ${t(Resources.Common.CurrencySign)}`,
										)
									: null
							}
							slots={{ barLabel: CustomBarLabel }}
						/>
					</ChartContainer>
				)}
			</div>
		</Stack>
	);
};
